import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { syncApi, TariffMapping, SyncProgress, ProductComparison, FamilySyncConfig } from '../lib/api';
import {
  RefreshCw, Settings, Search, Play, StopCircle, CheckCircle, XCircle,
  AlertTriangle, ArrowRight, Package, ChevronDown, ChevronUp, Loader2, Save, FolderCog
} from 'lucide-react';
import { useToast } from '@/components/ui/Toast';

export default function SyncPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  // UI State
  const [activeTab, setActiveTab] = useState<'reference' | 'family' | 'complete'>('reference');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReference, setSelectedReference] = useState('');
  const [selectedFamily, setSelectedFamily] = useState('');
  const [syncStock, setSyncStock] = useState(true);
  const [syncPrices, setSyncPrices] = useState(true);
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [showFamilyConfigPanel, setShowFamilyConfigPanel] = useState(false);

  // Local state for tariff mapping edits
  const [localMapping, setLocalMapping] = useState<TariffMapping | null>(null);

  // Local state for family sync config
  const [localFamilyConfig, setLocalFamilyConfig] = useState<FamilySyncConfig>({});

  // WebSocket for progress
  const [wsProgress, setWsProgress] = useState<SyncProgress | null>(null);

  // Queries
  const { data: tariffData, isLoading: tariffLoading } = useQuery({
    queryKey: ['tariffMapping'],
    queryFn: syncApi.getTariffMapping,
  });

  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ['customerGroups'],
    queryFn: syncApi.getCustomerGroups,
  });

  const { data: familiesData, isLoading: familiesLoading } = useQuery({
    queryKey: ['syncFamilies'],
    queryFn: syncApi.getFamilies,
  });

  const { data: familyConfigData, isLoading: familyConfigLoading } = useQuery({
    queryKey: ['familySyncConfig'],
    queryFn: syncApi.getFamilySyncConfig,
  });

  const { data: searchResults, isFetching: searching } = useQuery({
    queryKey: ['syncSearch', searchTerm],
    queryFn: () => syncApi.searchProducts(searchTerm),
    enabled: searchTerm.length >= 2,
  });

  const { data: comparisonData, isLoading: comparisonLoading, refetch: refetchComparison } = useQuery({
    queryKey: ['comparison', selectedReference],
    queryFn: () => syncApi.getComparison(selectedReference),
    enabled: !!selectedReference,
  });

  const { data: progressData } = useQuery({
    queryKey: ['syncProgress'],
    queryFn: syncApi.getProgress,
    refetchInterval: (query) => {
      const isRunning = wsProgress?.status === 'running' || query.state.data?.progress?.status === 'running';
      return isRunning ? 1000 : 5000;
    },
  });

  // Initialize local mapping when data loads
  useEffect(() => {
    if (tariffData?.mapping && !localMapping) {
      setLocalMapping(tariffData.mapping);
    }
  }, [tariffData, localMapping]);

  // Initialize local family config when data loads
  useEffect(() => {
    if (familyConfigData?.config) {
      setLocalFamilyConfig(familyConfigData.config);
    }
  }, [familyConfigData]);

  // WebSocket connection
  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout>;

    const connect = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.hostname}:8000/ws/sync-progress`;

      try {
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('WebSocket connected');
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'progress') {
              setWsProgress(data.data);
            }
          } catch (e) {
            console.error('WebSocket parse error:', e);
          }
        };

        ws.onclose = () => {
          console.log('WebSocket disconnected');
          reconnectTimeout = setTimeout(connect, 5000);
        };

        ws.onerror = () => {
          // Silent error, will reconnect
        };
      } catch (e) {
        console.error('WebSocket connection error:', e);
      }
    };

    connect();

    return () => {
      if (ws) {
        ws.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, []);

  // Mutations
  const saveMappingMutation = useMutation({
    mutationFn: (mapping: TariffMapping) => syncApi.saveTariffMapping(mapping),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tariffMapping'] });
      toast.success('Configuracion guardada');
    },
    onError: (error: Error) => {
      toast.error('Error guardando configuracion', error.message);
    },
  });

  const saveFamilyConfigMutation = useMutation({
    mutationFn: (config: FamilySyncConfig) => syncApi.saveFamilySyncConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['familySyncConfig'] });
      toast.success('Configuracion de familias guardada');
    },
    onError: (error: Error) => {
      toast.error('Error guardando configuracion de familias', error.message);
    },
  });

  const syncProductMutation = useMutation({
    mutationFn: (reference: string) => syncApi.syncProduct(reference, { sync_stock: syncStock, sync_prices: syncPrices }),
    onSuccess: () => {
      refetchComparison();
      queryClient.invalidateQueries({ queryKey: ['syncProgress'] });
    },
  });

  const syncFamilyMutation = useMutation({
    mutationFn: (family_code: string) => syncApi.syncByFamily(family_code, syncStock, syncPrices),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['syncProgress'] });
    },
  });

  const syncAllMutation = useMutation({
    mutationFn: () => syncApi.syncAll(syncStock, syncPrices),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['syncProgress'] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: syncApi.cancelSync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['syncProgress'] });
    },
  });

  // Current progress (prefer WebSocket, fallback to polling)
  const progress = wsProgress || progressData?.progress;
  const isRunning = progress?.status === 'running';

  // Handlers
  const handleSaveMapping = () => {
    if (localMapping) {
      saveMappingMutation.mutate(localMapping);
    }
  };

  const handleMappingChange = (tariffKey: keyof TariffMapping, groupId: number | null) => {
    if (!localMapping) return;

    setLocalMapping({
      ...localMapping,
      [tariffKey]: {
        ...localMapping[tariffKey],
        group_id: groupId,
      },
    });
  };

  const handleFamilyConfigChange = (familyCode: string, field: 'sync_stock' | 'sync_prices', value: boolean) => {
    setLocalFamilyConfig(prev => ({
      ...prev,
      [familyCode]: {
        sync_stock: field === 'sync_stock' ? value : (prev[familyCode]?.sync_stock ?? true),
        sync_prices: field === 'sync_prices' ? value : (prev[familyCode]?.sync_prices ?? true),
      }
    }));
  };

  const handleSaveFamilyConfig = () => {
    saveFamilyConfigMutation.mutate(localFamilyConfig);
  };

  const handleSelectAllFamilies = (field: 'sync_stock' | 'sync_prices', value: boolean) => {
    const updatedConfig: FamilySyncConfig = {};
    families.forEach(f => {
      updatedConfig[f.code] = {
        sync_stock: field === 'sync_stock' ? value : (localFamilyConfig[f.code]?.sync_stock ?? true),
        sync_prices: field === 'sync_prices' ? value : (localFamilyConfig[f.code]?.sync_prices ?? true),
      };
    });
    setLocalFamilyConfig(updatedConfig);
  };

  const comparison = comparisonData?.comparison;
  const groups = groupsData?.groups || [];
  const families = familiesData?.families || [];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sincronizacion Factusol - PrestaShop</h1>
          <p className="mt-1 text-slate-600">
            Sincroniza stock y tarifas desde Factusol hacia PrestaShop
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => { setShowConfigPanel(!showConfigPanel); setShowFamilyConfigPanel(false); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors border ${
              showConfigPanel
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-slate-300 text-gray-700 hover:bg-slate-50'
            }`}
          >
            <Settings className="w-5 h-5" />
            <span className="font-medium">Configurar Tarifas</span>
            {showConfigPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            onClick={() => { setShowFamilyConfigPanel(!showFamilyConfigPanel); setShowConfigPanel(false); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors border ${
              showFamilyConfigPanel
                ? 'bg-purple-50 border-purple-300 text-purple-700'
                : 'bg-white border-slate-300 text-gray-700 hover:bg-slate-50'
            }`}
          >
            <FolderCog className="w-5 h-5" />
            <span className="font-medium">Configurar Familias</span>
            {showFamilyConfigPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Configuration Panel (Collapsible) */}
      {showConfigPanel && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-slate-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Configuracion de Tarifas</h2>
            <p className="text-sm text-slate-500 mt-1">
              Asigna cada tarifa de Factusol a un grupo de clientes de PrestaShop.
              La tarifa 1 actualiza el precio principal del producto.
            </p>
          </div>

          <div className="p-6">
            {tariffLoading || groupsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                <span className="ml-2 text-slate-500">Cargando configuracion...</span>
              </div>
            ) : localMapping ? (
              <div className="space-y-4">
                {/* Tariff 1 - Principal */}
                <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{localMapping.tariff_1.name}</span>
                      <span className="px-2 py-0.5 bg-primary-600 text-white text-xs rounded-full font-medium">
                        Precio Principal
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      Esta tarifa actualiza el precio base del producto en PrestaShop
                    </p>
                  </div>
                  <div className="w-64">
                    <div className="px-4 py-2 bg-blue-100 rounded-lg text-blue-800 text-center font-medium">
                      Precio Principal (ps_product.price)
                    </div>
                  </div>
                </div>

                {/* Tariff 2 */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <span className="font-semibold text-gray-900">{localMapping.tariff_2.name}</span>
                    <p className="text-sm text-slate-600 mt-1">
                      Precio especifico para un grupo de clientes
                    </p>
                  </div>
                  <div className="w-64">
                    <select
                      value={localMapping.tariff_2.group_id || ''}
                      onChange={(e) => handleMappingChange('tariff_2', e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-blue-500 text-gray-900"
                    >
                      <option value="">-- No sincronizar --</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name} (ID: {g.id})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Tariff 3 */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <span className="font-semibold text-gray-900">{localMapping.tariff_3.name}</span>
                    <p className="text-sm text-slate-600 mt-1">
                      Precio especifico para un grupo de clientes
                    </p>
                  </div>
                  <div className="w-64">
                    <select
                      value={localMapping.tariff_3.group_id || ''}
                      onChange={(e) => handleMappingChange('tariff_3', e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-blue-500 text-gray-900"
                    >
                      <option value="">-- No sincronizar --</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name} (ID: {g.id})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSaveMapping}
                    disabled={saveMappingMutation.isPending}
                    className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-medium transition-colors"
                  >
                    {saveMappingMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Guardar Configuracion
                  </button>
                </div>

                {saveMappingMutation.isSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span>Configuracion guardada correctamente</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                No se pudo cargar la configuracion
              </div>
            )}
          </div>
        </div>
      )}

      {/* Family Configuration Panel (Collapsible) */}
      {showFamilyConfigPanel && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-purple-50 px-6 py-4 border-b border-purple-200">
            <h2 className="text-lg font-semibold text-gray-900">Configuracion de Familias</h2>
            <p className="text-sm text-slate-500 mt-1">
              Selecciona que datos sincronizar para cada familia de productos.
              Por defecto, todas las familias sincronizan stock y precios.
            </p>
          </div>

          <div className="p-6">
            {familiesLoading || familyConfigLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                <span className="ml-2 text-slate-500">Cargando familias...</span>
              </div>
            ) : families.length > 0 ? (
              <div className="space-y-4">
                {/* Header with select all */}
                <div className="flex items-center p-3 bg-slate-100 rounded-lg border border-gray-200">
                  <div className="flex-1 font-semibold text-gray-700">Familia</div>
                  <div className="flex items-center">
                    <div className="w-28 flex flex-col items-center gap-1">
                      <span className="text-xs font-semibold text-slate-600 uppercase">Stock</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleSelectAllFamilies('sync_stock', true)}
                          className="text-xs px-2 py-1 bg-success-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                        >
                          Todos
                        </button>
                        <button
                          onClick={() => handleSelectAllFamilies('sync_stock', false)}
                          className="text-xs px-2 py-1 bg-danger-100 text-danger-700 rounded hover:bg-red-200 transition-colors"
                        >
                          Ninguno
                        </button>
                      </div>
                    </div>
                    <div className="w-28 flex flex-col items-center gap-1">
                      <span className="text-xs font-semibold text-slate-600 uppercase">Precios</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleSelectAllFamilies('sync_prices', true)}
                          className="text-xs px-2 py-1 bg-success-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                        >
                          Todos
                        </button>
                        <button
                          onClick={() => handleSelectAllFamilies('sync_prices', false)}
                          className="text-xs px-2 py-1 bg-danger-100 text-danger-700 rounded hover:bg-red-200 transition-colors"
                        >
                          Ninguno
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Families list */}
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  {families.map((family, index) => {
                    const config = localFamilyConfig[family.code] || { sync_stock: true, sync_prices: true };
                    return (
                      <div
                        key={family.code}
                        className={`flex items-center p-3 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                        } ${index !== families.length - 1 ? 'border-b border-gray-100' : ''}`}
                      >
                        <div className="flex-1 flex items-center gap-3">
                          <span className="font-mono text-sm text-purple-600 font-medium bg-purple-100 px-2 py-0.5 rounded">
                            {family.code}
                          </span>
                          <span className="text-gray-700">{family.name}</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-28 flex justify-center">
                            <input
                              type="checkbox"
                              checked={config.sync_stock}
                              onChange={(e) => handleFamilyConfigChange(family.code, 'sync_stock', e.target.checked)}
                              className="w-5 h-5 text-success-600 rounded border-slate-300 focus:ring-green-500 cursor-pointer"
                            />
                          </div>
                          <div className="w-28 flex justify-center">
                            <input
                              type="checkbox"
                              checked={config.sync_prices}
                              onChange={(e) => handleFamilyConfigChange(family.code, 'sync_prices', e.target.checked)}
                              className="w-5 h-5 text-primary-600 rounded border-slate-300 focus:ring-primary-500 cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSaveFamilyConfig}
                    disabled={saveFamilyConfigMutation.isPending}
                    className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium transition-colors"
                  >
                    {saveFamilyConfigMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Guardar Configuracion
                  </button>
                </div>

                {saveFamilyConfigMutation.isSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span>Configuracion de familias guardada correctamente</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                No se pudieron cargar las familias
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sync Options */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-8">
          <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Opciones de sincronizacion:</span>
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={syncStock}
              onChange={(e) => setSyncStock(e.target.checked)}
              className="w-5 h-5 text-primary-600 rounded border-slate-300 focus:ring-primary-500"
            />
            <span className="text-gray-700 group-hover:text-gray-900 font-medium">Sincronizar Stock</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={syncPrices}
              onChange={(e) => setSyncPrices(e.target.checked)}
              className="w-5 h-5 text-primary-600 rounded border-slate-300 focus:ring-primary-500"
            />
            <span className="text-gray-700 group-hover:text-gray-900 font-medium">Sincronizar Precios/Tarifas</span>
          </label>
        </div>
      </div>

      {/* Progress Panel (shown when running or completed) */}
      {progress && progress.status !== 'idle' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className={`px-6 py-4 border-b ${
            isRunning ? 'bg-blue-50 border-blue-200' :
            progress.status === 'completed' ? 'bg-green-50 border-green-200' :
            progress.status === 'cancelled' ? 'bg-orange-50 border-orange-200' :
            'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                {isRunning ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin text-primary-600" />
                    <span className="text-blue-900">Sincronizando...</span>
                  </>
                ) : progress.status === 'completed' ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-success-600" />
                    <span className="text-green-900">Sincronizacion Completada</span>
                  </>
                ) : progress.status === 'cancelled' ? (
                  <>
                    <XCircle className="w-5 h-5 text-orange-600" />
                    <span className="text-orange-900">Sincronizacion Cancelada</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 text-danger-600" />
                    <span className="text-red-900">Error en Sincronizacion</span>
                  </>
                )}
              </h2>
              {isRunning && (
                <button
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50 font-medium"
                >
                  <StopCircle className="w-4 h-4" />
                  Cancelar
                </button>
              )}
            </div>
          </div>

          <div className="p-6">
            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-slate-600 mb-2">
                <span className="font-medium">{progress.processed} de {progress.total} productos procesados</span>
                <span className="font-bold text-lg">{progress.percent}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden">
                <div
                  className={`h-4 rounded-full transition-all duration-300 ${
                    progress.status === 'completed' ? 'bg-success-500' :
                    progress.status === 'cancelled' ? 'bg-orange-500' :
                    progress.status === 'error' ? 'bg-danger-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg text-center border border-gray-200">
                <div className="text-3xl font-bold text-gray-900">{progress.total}</div>
                <div className="text-sm text-slate-500 font-medium mt-1">Total</div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center border border-blue-200">
                <div className="text-3xl font-bold text-primary-600">{progress.processed}</div>
                <div className="text-sm text-primary-600 font-medium mt-1">Procesados</div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center border border-green-200">
                <div className="text-3xl font-bold text-success-600">{progress.success}</div>
                <div className="text-sm text-success-600 font-medium mt-1">Exitosos</div>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center border border-red-200">
                <div className="text-3xl font-bold text-danger-600">{progress.errors}</div>
                <div className="text-sm text-danger-600 font-medium mt-1">Errores</div>
              </div>
            </div>

            {/* Current Reference */}
            {isRunning && progress.current_reference && (
              <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                <span className="text-sm text-slate-600">Procesando: </span>
                <span className="font-mono font-bold text-gray-900">{progress.current_reference}</span>
              </div>
            )}

            {/* Error Details */}
            {progress.error_details && progress.error_details.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-danger-700 mb-2">Ultimos errores:</h3>
                <div className="max-h-32 overflow-y-auto space-y-1 bg-red-50 rounded-lg p-3 border border-red-200">
                  {progress.error_details.slice(-5).map((err, i) => (
                    <div key={i} className="text-sm text-danger-700">
                      <span className="font-mono font-medium">{err.reference}</span>
                      <span className="mx-2">-</span>
                      <span>{err.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 bg-slate-50">
          <nav className="flex">
            {[
              { id: 'reference' as const, label: 'Sincronizar por Referencia', icon: Search },
              { id: 'family' as const, label: 'Sincronizar por Familia', icon: Package },
              { id: 'complete' as const, label: 'Sincronizacion Completa', icon: RefreshCw },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition-colors border-b-2 ${
                    isActive
                      ? 'border-blue-600 text-primary-600 bg-white'
                      : 'border-transparent text-slate-500 hover:text-gray-700 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Reference Tab */}
          {activeTab === 'reference' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Buscar articulo en Factusol (por codigo o descripcion)
                </label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar en Factusol: codigo de articulo o descripcion..."
                    className="w-full pl-12 pr-12 py-3 text-gray-900 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-blue-500 placeholder-gray-400"
                  />
                  {searching && (
                    <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-600 animate-spin" />
                  )}
                </div>

                {/* Search Results Dropdown */}
                {searchResults?.products && searchResults.products.length > 0 && searchTerm.length >= 2 && (
                  <div className="mt-2 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.products.map((p) => (
                      <button
                        key={p.reference}
                        onClick={() => {
                          setSelectedReference(p.reference);
                          setSearchTerm('');
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <span className="font-mono text-primary-600 font-medium">{p.reference}</span>
                        <span className="ml-3 text-slate-600">{p.name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {searchTerm.length >= 2 && !searching && searchResults?.products?.length === 0 && (
                  <div className="mt-2 p-4 bg-slate-50 rounded-lg text-slate-500 text-center">
                    No se encontraron productos con "{searchTerm}"
                  </div>
                )}
              </div>

              {/* Selected Reference Badge */}
              {selectedReference && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between">
                  <div>
                    <span className="text-sm text-primary-600 font-medium">Referencia seleccionada:</span>
                    <span className="ml-3 font-mono font-bold text-blue-900 text-lg">{selectedReference}</span>
                  </div>
                  <button
                    onClick={() => setSelectedReference('')}
                    className="p-1 text-blue-400 hover:text-primary-600 hover:bg-blue-100 rounded transition-colors"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              )}

              {/* Comparison Results */}
              {selectedReference && (
                <>
                  {comparisonLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                      <span className="ml-3 text-slate-500">Cargando comparacion...</span>
                    </div>
                  ) : comparison ? (
                    <div className="space-y-4">
                      {/* Status Cards */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className={`p-5 rounded-lg border-2 ${
                          comparison.found_in_factusol
                            ? 'bg-green-50 border-green-300'
                            : 'bg-red-50 border-red-300'
                        }`}>
                          <div className="flex items-center gap-2 mb-3">
                            {comparison.found_in_factusol ? (
                              <CheckCircle className="w-6 h-6 text-success-600" />
                            ) : (
                              <XCircle className="w-6 h-6 text-danger-600" />
                            )}
                            <span className="font-bold text-gray-900 text-lg">Factusol</span>
                          </div>
                          {comparison.factusol ? (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-600">Stock:</span>
                                <span className="font-bold text-gray-900">{comparison.factusol.stock} uds</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-600">Tarifa 1 (PVP):</span>
                                <span className="font-bold text-gray-900">{Number(comparison.factusol.tariffs?.tariff_1 ?? 0).toFixed(2)} €</span>
                              </div>
                              {localMapping?.tariff_2?.group_id && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">{localMapping.tariff_2.name}:</span>
                                  <span className="font-bold text-gray-900">{Number(comparison.factusol.tariffs?.tariff_2 ?? 0).toFixed(2)} €</span>
                                </div>
                              )}
                              {localMapping?.tariff_3?.group_id && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">{localMapping.tariff_3.name}:</span>
                                  <span className="font-bold text-gray-900">{Number(comparison.factusol.tariffs?.tariff_3 ?? 0).toFixed(2)} €</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-danger-600 text-sm">Producto no encontrado</p>
                          )}
                        </div>

                        <div className={`p-5 rounded-lg border-2 ${
                          comparison.found_in_prestashop
                            ? 'bg-green-50 border-green-300'
                            : 'bg-red-50 border-red-300'
                        }`}>
                          <div className="flex items-center gap-2 mb-3">
                            {comparison.found_in_prestashop ? (
                              <CheckCircle className="w-6 h-6 text-success-600" />
                            ) : (
                              <XCircle className="w-6 h-6 text-danger-600" />
                            )}
                            <span className="font-bold text-gray-900 text-lg">PrestaShop</span>
                          </div>
                          {comparison.prestashop && comparison.prestashop.products[0] ? (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-600">Stock:</span>
                                <span className="font-bold text-gray-900">{comparison.prestashop.products[0].stock} uds</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-600">Tarifa 1 (PVP):</span>
                                <span className="font-bold text-gray-900">{comparison.prestashop.prices?.principal_price?.toFixed(2) ?? '0.00'} €</span>
                              </div>
                              {localMapping?.tariff_2?.group_id && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">{localMapping.tariff_2.name}:</span>
                                  <span className="font-bold text-gray-900">
                                    {comparison.prestashop.prices?.group_prices?.[String(localMapping.tariff_2.group_id)]?.price?.toFixed(2) ?? '0.00'} €
                                  </span>
                                </div>
                              )}
                              {localMapping?.tariff_3?.group_id && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">{localMapping.tariff_3.name}:</span>
                                  <span className="font-bold text-gray-900">
                                    {comparison.prestashop.prices?.group_prices?.[String(localMapping.tariff_3.group_id)]?.price?.toFixed(2) ?? '0.00'} €
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-danger-600 text-sm">Producto no encontrado</p>
                          )}
                        </div>
                      </div>

                      {/* Differences */}
                      {comparison.differences && comparison.differences.length > 0 && (
                        <div className="border-2 border-orange-300 rounded-lg overflow-hidden">
                          <div className="bg-orange-50 px-4 py-3 border-b border-orange-200">
                            <h3 className="font-bold text-orange-800 flex items-center gap-2">
                              <AlertTriangle className="w-5 h-5" />
                              {comparison.differences.length} diferencia(s) detectada(s)
                            </h3>
                          </div>
                          <div className="divide-y divide-orange-100">
                            {comparison.differences.map((diff, i) => (
                              <div key={i} className="flex items-center justify-between p-4 bg-orange-50/50">
                                <span className="font-medium text-gray-700">{diff.name || diff.field}</span>
                                <div className="flex items-center gap-3">
                                  <span className="px-3 py-1 bg-slate-200 rounded text-gray-700 font-mono">
                                    PS: {typeof diff.prestashop === 'number' ? diff.prestashop.toFixed(2) : diff.prestashop}
                                  </span>
                                  <ArrowRight className="w-5 h-5 text-orange-500" />
                                  <span className="px-3 py-1 bg-green-200 rounded text-green-800 font-mono font-bold">
                                    FS: {typeof diff.factusol === 'number' ? diff.factusol.toFixed(2) : diff.factusol}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {comparison.differences?.length === 0 && comparison.found_in_factusol && comparison.found_in_prestashop && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                          <CheckCircle className="w-6 h-6 text-success-600" />
                          <span className="text-green-700 font-medium">Los datos estan sincronizados correctamente</span>
                        </div>
                      )}

                      {/* Sync Button */}
                      {comparison.found_in_factusol && comparison.found_in_prestashop && (
                        <button
                          onClick={() => syncProductMutation.mutate(selectedReference)}
                          disabled={syncProductMutation.isPending || isRunning}
                          className="w-full px-6 py-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-semibold text-lg transition-colors"
                        >
                          {syncProductMutation.isPending ? (
                            <>
                              <Loader2 className="w-6 h-6 animate-spin" />
                              Sincronizando...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-6 h-6" />
                              Sincronizar Este Producto
                            </>
                          )}
                        </button>
                      )}

                      {/* Sync Result */}
                      {syncProductMutation.isSuccess && (
                        <div className="p-4 bg-green-50 border border-green-300 rounded-lg flex items-center gap-3">
                          <CheckCircle className="w-6 h-6 text-success-600" />
                          <span className="font-medium text-green-700">Producto sincronizado correctamente</span>
                        </div>
                      )}
                    </div>
                  ) : null}
                </>
              )}

              {!selectedReference && (
                <div className="text-center py-12 text-slate-400">
                  <Search className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Busca un articulo de Factusol para ver la comparacion con PrestaShop</p>
                </div>
              )}
            </div>
          )}

          {/* Family Tab */}
          {activeTab === 'family' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Seleccionar Familia de Factusol
                </label>
                {familiesLoading ? (
                  <div className="flex items-center gap-2 p-4 bg-slate-50 rounded-lg">
                    <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                    <span className="text-slate-500">Cargando familias...</span>
                  </div>
                ) : (
                  <select
                    value={selectedFamily}
                    onChange={(e) => setSelectedFamily(e.target.value)}
                    className="w-full px-4 py-3 text-gray-900 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-blue-500"
                  >
                    <option value="">-- Selecciona una familia --</option>
                    {families.map((f) => (
                      <option key={f.code} value={f.code}>
                        {f.code} - {f.name}
                      </option>
                    ))}
                  </select>
                )}
                {families.length === 0 && !familiesLoading && (
                  <p className="mt-2 text-sm text-danger-500">No se pudieron cargar las familias de Factusol</p>
                )}
              </div>

              {selectedFamily && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-blue-800">
                    Se sincronizaran todos los productos de la familia <strong>{selectedFamily}</strong> que existan en PrestaShop.
                  </p>
                </div>
              )}

              <button
                onClick={() => selectedFamily && syncFamilyMutation.mutate(selectedFamily)}
                disabled={!selectedFamily || syncFamilyMutation.isPending || isRunning}
                className="w-full px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-semibold text-lg transition-colors"
              >
                {syncFamilyMutation.isPending || isRunning ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    Iniciando sincronizacion...
                  </>
                ) : (
                  <>
                    <Play className="w-6 h-6" />
                    Sincronizar Familia
                  </>
                )}
              </button>
            </div>
          )}

          {/* Complete Sync Tab */}
          {activeTab === 'complete' && (
            <div className="space-y-6">
              <div className="p-5 bg-amber-50 border-2 border-amber-300 rounded-lg">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-amber-800 text-lg">Sincronizacion Completa</h3>
                    <p className="text-amber-700 mt-2">
                      Esta accion sincronizara <strong>todos los productos</strong> que existen tanto en Factusol como en PrestaShop.
                      El proceso puede tardar varios minutos dependiendo del numero de productos.
                    </p>
                    <p className="text-amber-600 mt-2 text-sm">
                      Puedes cancelar la sincronizacion en cualquier momento.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => syncAllMutation.mutate()}
                disabled={syncAllMutation.isPending || isRunning}
                className="w-full px-6 py-5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-bold text-xl transition-colors"
              >
                {syncAllMutation.isPending || isRunning ? (
                  <>
                    <Loader2 className="w-7 h-7 animate-spin" />
                    Iniciando sincronizacion completa...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-7 h-7" />
                    Iniciar Sincronizacion Completa
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
